import { Link } from "wouter";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import SEO from "@/components/SEO";
import { organizationSchema, pageSEO } from "@/lib/seoConfig";
import { blogPosts } from "@/lib/blogData";

const BLOG_HERO =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/hero-packaging-products-jTcsMjjFhH8thZMMjwdh3M.webp";

export default function Blog() {
  const sortedPosts = [...blogPosts].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return (
    <div>
      <SEO
        title={pageSEO.insights.title}
        description={pageSEO.insights.description}
        keywords={pageSEO.insights.keywords}
        canonical={pageSEO.insights.canonical}
        structuredData={organizationSchema}
      />

      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={BLOG_HERO}
            alt="Packaging sales recruitment insights and industry advice from OC Recruitment"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F2137]/92 via-[#0F2137]/85 to-[#0F2137]/65" />
        </div>
        <div className="container relative z-10 pt-32 pb-16">
          <div className="max-w-2xl">
            <ScrollReveal>
              <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                Insights & Advice
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <h1 className="text-4xl sm:text-5xl text-white leading-[1.1] mb-6">
                Packaging Sales Recruitment Insights
              </h1>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <p className="text-lg text-white/70 leading-relaxed">
                Expert advice on hiring, career development, and talent market
                trends across the US packaging industry. Written by specialists
                who live and breathe packaging recruitment.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl">
            {sortedPosts.map((post, i) => (
              <ScrollReveal key={post.slug} delay={i * 0.1}>
                <Link
                  href={`/insights/${post.slug}`}
                  className="group block h-full p-8 rounded border border-border hover:border-steel/30 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs font-semibold uppercase tracking-wider text-steel bg-steel/10 px-3 py-1 rounded">
                      {post.category}
                    </span>
                  </div>
                  <h2 className="text-xl text-midnight mb-3 group-hover:text-steel transition-colors font-['DM_Serif_Display',Georgia,serif]">
                    {post.title}
                  </h2>
                  <p className="text-slate-brand text-sm leading-relaxed mb-6">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-xs text-slate-brand/70">
                      <span className="flex items-center gap-1.5">
                        <Calendar size={12} />
                        {new Date(post.date).toLocaleDateString("en-US", {
                          month: "long",
                          timeZone: "UTC",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Clock size={12} />
                        {post.readTime}
                      </span>
                    </div>
                    <ArrowRight
                      size={16}
                      className="text-steel opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all"
                    />
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-pale">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal>
              <h2 className="text-3xl sm:text-4xl text-midnight mb-6">
                Need Help With Your Packaging Sales Search?
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <p className="text-lg text-slate-brand leading-relaxed mb-8">
                Whether you are a packaging company looking to hire commercial
                talent or a sales professional exploring new opportunities, we
                are ready for a confidential conversation.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  href="/contact"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-midnight text-white text-sm font-semibold rounded hover:bg-midnight/90 transition-all duration-200 group"
                >
                  Hire Packaging Sales Talent
                  <ArrowRight
                    size={16}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </Link>
                <Link
                  href="/for-candidates"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-midnight/20 text-midnight text-sm font-semibold rounded hover:bg-midnight/5 transition-all duration-200"
                >
                  Submit Your Resume
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  );
}
